import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import TheatersIcon from "@mui/icons-material/Theaters";
import { Box, IconButton, Typography } from "@mui/material";
import { memo } from "react";
import CardMediaVideoPlayer from "../../../../../media-viewer/components/player/CardMediaVideoPlayer";
import { S3File } from "../../../../../telemetry/hooks/useGetS3Files";
import { formatRecordingDisplayName } from "./formatRecordingDisplayName";

interface LiveViewRecordingsVideoListItemProps {
  file: S3File;
  timeZone: string;
  idOfPlayingVideo: string;
  setIdOfPlayingVideo: (idOfPlayingVideo: string) => void;
}

const LiveViewRecordingsVideoListItem = ({
  file,
  timeZone,
  idOfPlayingVideo,
  setIdOfPlayingVideo,
}: LiveViewRecordingsVideoListItemProps): JSX.Element => {
  const isPlaying = file.key === idOfPlayingVideo;

  return (
    <Box
      key={file.key}
      role="listitem"
      sx={{
        border: theme => `1px solid ${theme.palette.divider}`,
        borderRadius: "10px",
        px: 1.5,
        py: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "start",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              border: theme => `1px solid ${theme.palette.divider}`,
              backgroundColor: theme => theme.palette.otherBackground.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <TheatersIcon fontSize="small" color="action" />
          </Box>
          <Box>
            <Typography variant="body1">
              {formatRecordingDisplayName(file.filename, timeZone)}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {typeof file.size === "number"
                ? `${(file.size / (1024 * 1024)).toFixed(0)}MB`
                : "-"}
            </Typography>
          </Box>
        </Box>
        <Box>
          {isPlaying ? (
            <IconButton
              size="medium"
              onClick={e => {
                e.stopPropagation();

                setIdOfPlayingVideo("");
              }}
            >
              <ExpandLessOutlinedIcon fontSize="medium" />
            </IconButton>
          ) : (
            <IconButton
              size="medium"
              onClick={e => {
                e.stopPropagation();

                setIdOfPlayingVideo(file.key);
              }}
            >
              <ExpandMoreOutlinedIcon fontSize="medium" />
            </IconButton>
          )}

          <IconButton
            size="medium"
            component="a"
            href={file.signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
            onClick={e => e.stopPropagation()}
          >
            <FileDownloadOutlinedIcon fontSize="medium" />
          </IconButton>
        </Box>
      </Box>

      {isPlaying && (
        <Box
          sx={{
            width: "100%",
            height: "280px",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            textAlign: "center",
            paddingTop: 2,
          }}
        >
          <CardMediaVideoPlayer
            src={file.signedUrl}
            controls
            autoPlay
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "8px",
              backgroundColor: "black",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(LiveViewRecordingsVideoListItem);
